// The recovery offer on the machine tab (FR-52; Dominik 19.10.2026: "wenn sowieso alles in der cloud gespeichert ist,
// weshalb bieten wir eine sicherung an?"): after a data loss or when a coupling continues an earlier one, once: "Die
// Maschine hat ihre Einstellungen verloren. Aus der Cloud wiederherstellen?". One tap takes it over (the
// protected values get their own question first), "So lassen" keeps the machine's. Members only see that the
// owner decides. Decisions in core/screens/device.ts, the offer from the API (core/device.ts).
import { useEffect, useId, useState } from 'react';
import type { RecoveryResponse } from '@brewcore/app-contract';
import { reasonText, recoveryView, restoreProblems, t, type RestoreLine } from '../../core/index.js';
import { Banner, Button, Sheet } from '../components/index.js';
import { useMachine } from '../machine-context.js';
import { useDeviceApi } from './use-device.js';

export function RecoveryOffer() {
  const { ctx, machineId } = useMachine();
  const api = useDeviceApi();
  const lost = ctx.view.dataLost;
  const titleId = useId();
  const [offer, setOffer] = useState<RecoveryResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [asking, setAsking] = useState<RestoreLine[] | null>(null);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [busy, setBusy] = useState<'take' | 'keep' | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // read once per offer (the flag dataLost comes with the snapshot and goes off for everyone when it is decided)
  useEffect(() => {
    if (!lost) {
      setOffer(null);
      setMissing(false);
      return;
    }
    let live = true;
    void api.recovery(machineId).then((r) => {
      if (!live) return;
      if (r.ok) setOffer(r.value);
      else setMissing(true);
    });
    return () => {
      live = false;
    };
  }, [lost, api, machineId]);
  if (!lost) return null;
  // no known state in the cloud: only the note that data was lost (the mirror shows the machine)
  if (missing || !offer)
    return missing ? (
      <Banner tone="warn" icon="alert" data-testid="data-lost">
        {ctx.tr('dataLost')}
      </Banner>
    ) : null;
  const v = recoveryView(ctx, offer);
  const send = async (yes: string[]) => {
    setBusy('take');
    setError(null);
    setProblems([]);
    const params = v.lines.filter((l) => !l.protected || yes.includes(l.id)).map((l) => l.id);
    const r = await api.recover(machineId, params, yes);
    setBusy(null);
    if (!r.ok) {
      setError(
        r.error.code === 'fresh_login' ? t(ctx.lang, 'freshNeeded') : reasonText(ctx.lang, r.error.code),
      );
      return;
    }
    // closed: the flag goes off and the offer leaves by itself; refused groups stay listed
    setProblems(restoreProblems(ctx, r.value.results));
  };
  const take = () => {
    const s = v.lines.filter((l) => l.protected);
    setConfirmed([]);
    if (s.length) setAsking(s);
    else void send([]);
  };
  const answer = (yes: boolean) => {
    const [head, ...rest] = asking ?? [];
    const next = yes && head ? [...confirmed, head.id] : confirmed;
    setConfirmed(next);
    if (rest.length) setAsking(rest);
    else {
      setAsking(null);
      void send(next);
    }
  };
  const keep = async () => {
    setBusy('keep');
    setError(null);
    const r = await api.dismissRecovery(machineId);
    setBusy(null);
    if (!r.ok) setError(reasonText(ctx.lang, r.error.code));
  };
  const question = asking?.[0];
  return (
    <div className="recovery" data-testid="recovery">
      <Banner tone="warn" icon="alert" data-testid="data-lost">
        <strong>{v.question}</strong> {v.detail}
      </Banner>
      {!v.canDecide && <p className="hint">{ctx.tr('recoveryOwner')}</p>}
      {v.canDecide && (
        <div className="btn-row">
          <Button
            size="sm"
            busy={busy === 'take'}
            disabled={busy !== null || !ctx.controllable}
            onClick={take}
            data-testid="recovery-take"
          >
            {v.take}
          </Button>
          {v.lines.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              busy={busy === 'keep'}
              disabled={busy !== null}
              onClick={() => void keep()}
              data-testid="recovery-keep"
            >
              {v.keep}
            </Button>
          )}
        </div>
      )}
      {problems.map((p) => (
        <Banner key={p} tone="warn" data-testid="recovery-problem">
          {ctx.tr('recoveryRefused', { p })}
        </Banner>
      ))}
      {error && (
        <p className="field-error" role="alert" data-testid="recovery-error">
          {error}
        </p>
      )}
      {question && (
        <Sheet
          variant="alert"
          title={ctx.tr('recoveryConfirmSTitle')}
          titleId={titleId}
          onClose={() => answer(false)}
          testId="recovery-confirm-sheet"
        >
          <p className="text-dim">
            {ctx.tr('recoveryConfirmS', { l: question.label, a: question.from, b: question.to })}
          </p>
          <div className="sheet-actions">
            <Button variant="danger" block onClick={() => answer(true)} data-testid="recovery-confirm-yes">
              {ctx.tr('recoveryTake')}
            </Button>
            <Button variant="secondary" block onClick={() => answer(false)} data-testid="recovery-confirm-no">
              {ctx.tr('recoverySkip')}
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
